import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface PlaygroundResponse {
  message: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

export function AIPlayground() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const mutation = useMutation({
    mutationFn: async (userMessage: string) => {
      const conversationHistory = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));
      return api.post<PlaygroundResponse>('/api/ai-settings/test', {
        message: userMessage,
        conversationHistory,
      });
    },
    onSuccess: (data) => {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.message },
      ]);
    },
    onError: () => {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '⚠️ Error: Failed to get AI response. Check your DeepSeek API key and AI config.' },
      ]);
    },
  });

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || mutation.isPending) return;

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    mutation.mutate(trimmed);
  };

  const handleReset = () => {
    setMessages([]);
    setInput('');
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">AI Playground</CardTitle>
          <CardDescription>
            Test your AI agent with the current system prompt and settings
          </CardDescription>
        </div>
        {messages.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleReset}>
            Clear Chat
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {/* Chat area */}
        <div className="border rounded-lg bg-muted/30 h-[400px] flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                Send a message to test your AI agent configuration
              </div>
            )}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border shadow-sm'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}
            {mutation.isPending && (
              <div className="flex justify-start">
                <div className="bg-card border shadow-sm rounded-lg px-4 py-2.5 text-sm">
                  <span className="inline-flex gap-1">
                    <span className="animate-bounce" style={{ animationDelay: '0ms' }}>●</span>
                    <span className="animate-bounce" style={{ animationDelay: '150ms' }}>●</span>
                    <span className="animate-bounce" style={{ animationDelay: '300ms' }}>●</span>
                  </span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t p-3 flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message to test..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              disabled={mutation.isPending}
            />
            <Button onClick={handleSend} disabled={!input.trim() || mutation.isPending}>
              Send
            </Button>
          </div>
        </div>

        {/* Token usage display */}
        {mutation.data?.usage && (
          <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
            <span>Prompt: {mutation.data.usage.prompt_tokens} tokens</span>
            <span>Completion: {mutation.data.usage.completion_tokens} tokens</span>
            <span>Total: {mutation.data.usage.total_tokens} tokens</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
